import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';

export interface CreditWallet {
  id: string;
  workspace_id: string;
  credits_balance: number;
  credits_reserved: number;
  low_balance_threshold: number;
  critical_balance_threshold: number;
  auto_topup_threshold: number;
  auto_topup_amount: number;
  auto_topup_enabled: boolean;
  auto_topup_stripe_payment_method_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreditTransaction {
  id: string;
  workspace_id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  usage_type?: string;
  usage_details?: any;
  stripe_payment_intent_id?: string;
  stripe_invoice_id?: string;
  reference_id?: string;
  reference_type?: string;
  description?: string;
  created_at: string;
  created_by?: string;
}

interface CreditOperationResult {
  success: boolean;
  new_balance?: number;
  amount_consumed?: number;
  amount_added?: number;
  error?: string;
  current_balance?: number;
  required_amount?: number;
}

export const useWorkspaceCredits = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  // Get credit wallet
  const {
    data: creditWallet,
    isLoading: isLoadingWallet,
    error: walletError
  } = useQuery<CreditWallet>({
    queryKey: ['workspace-credits', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('workspace_credit_wallets')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Get credit transactions
  const {
    data: transactions,
    isLoading: isLoadingTransactions
  } = useQuery({
    queryKey: ['credit-transactions', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data as CreditTransaction[];
    },
    enabled: !!currentWorkspace?.id
  });

  // Consume credits mutation
  const consumeCredits = useMutation({
    mutationFn: async ({
      amount,
      usageType,
      referenceId,
      description
    }: {
      amount: number;
      usageType: string;
      referenceId?: string;
      description?: string;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.rpc('consume_workspace_credits', {
        workspace_id_param: currentWorkspace.id,
        credit_amount: amount,
        usage_type_param: usageType,
        reference_id_param: referenceId,
        description_param: description
      });

      if (error) throw error;
      return data as unknown as CreditOperationResult;
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['workspace-credits'] });
        queryClient.invalidateQueries({ queryKey: ['credit-transactions'] });
        toast.success(`${result.amount_consumed} credits consumed`);
      } else if (result.error === 'insufficient_credits') {
        toast.error('Insufficient credits! Please top up your account.');
      }
    },
    onError: (error: any) => {
      toast.error(`Failed to consume credits: ${error.message}`);
    }
  });

  // Top up credits mutation
  const topUpCredits = useMutation({
    mutationFn: async ({
      amount,
      paymentIntentId,
      description
    }: {
      amount: number;
      paymentIntentId?: string;
      description?: string;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.rpc('topup_workspace_credits', {
        workspace_id_param: currentWorkspace.id,
        credit_amount: amount,
        payment_intent_id: paymentIntentId,
        description_param: description
      });

      if (error) throw error;
      return data as unknown as CreditOperationResult;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-credits'] });
      queryClient.invalidateQueries({ queryKey: ['credit-transactions'] });
      toast.success(`${result.amount_added} credits added successfully!`);
    },
    onError: (error: any) => {
      toast.error(`Failed to add credits: ${error.message}`);
    }
  });

  // Update wallet settings mutation
  const updateWalletSettings = useMutation({
    mutationFn: async (settings: Partial<CreditWallet>) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase
        .from('workspace_credit_wallets')
        .update(settings)
        .eq('workspace_id', currentWorkspace.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-credits'] });
      toast.success('Wallet settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update settings: ${error.message}`);
    }
  });

  // Helper functions
  const getBalanceStatus = () => {
    if (!creditWallet) return { color: 'bg-muted text-muted-foreground', status: 'Unknown', urgent: false };
    
    const balance = creditWallet.credits_balance;
    const critical = creditWallet.critical_balance_threshold;
    const low = creditWallet.low_balance_threshold;
    
    if (balance <= critical) {
      return { color: 'bg-destructive/10 text-destructive', status: 'Critical', urgent: true };
    } else if (balance <= low) {
      return { color: 'bg-warning/10 text-warning', status: 'Low', urgent: false };
    } else {
      return { color: 'bg-success/10 text-success', status: 'Good', urgent: false };
    }
  };

  const estimateUsageRemaining = (creditsPerOperation: number = 1) => {
    if (!creditWallet) return 0;
    return Math.floor(creditWallet.credits_balance / creditsPerOperation);
  };

  const canAfford = (amount: number) => {
    if (!creditWallet) return false;
    return creditWallet.credits_balance >= amount;
  };

  return {
    // Data
    creditWallet,
    transactions,
    
    // Loading states
    isLoadingWallet,
    isLoadingTransactions,
    
    // Mutations
    consumeCredits,
    topUpCredits,
    updateWalletSettings,
    
    // Helper functions
    getBalanceStatus,
    estimateUsageRemaining,
    canAfford,
    
    // Loading states for mutations
    isConsuming: consumeCredits.isPending,
    isToppingUp: topUpCredits.isPending,
    isUpdatingSettings: updateWalletSettings.isPending,
    
    // Error state
    error: walletError
  };
};